// ============================================================
// The Structures tool: pillars, cones and domes
// ============================================================

import common from '../../styles/common.module.css';
import styles from '../../styles/Battlefield.module.css';
import SliderInput from '../SliderInput';
import { STRUCTURE_SHAPES } from '../../engine/battlefield/structures';
import { RadioChip } from './controls';

/**
 * A new radius for the selected structure. A cone keeps its point: a top
 * radius of zero stays zero, anything else follows the bottom.
 */
const radiusPatch = (selected, value) => ({ radiusBottom: value, radiusTop: selected.radiusTop === 0 ? 0 : value });

/**
 * THE SAME SLIDERS DESCRIBE THE NEXT ONE AND THE SELECTED ONE. With
 * something selected they edit it; with nothing selected they set what the
 * next click will lay down - how a paint program's brush settings behave,
 * with no second set of controls to keep in step.
 */
function structureSliders(build) {
  const selected = build.selectedStructure;
  return [
    {
      label: 'Radius',
      id: 'structure-radius',
      min: 0.2,
      max: 20,
      step: 0.1,
      float: true,
      unit: 'm',
      value: selected?.radiusBottom ?? build.structureRadius,
      set: build.setStructureRadius,
      patch: (v) => radiusPatch(selected, v),
    },
    {
      label: 'Height',
      id: 'structure-height',
      min: 0.1,
      max: 40,
      step: 0.1,
      float: true,
      unit: 'm',
      value: selected?.height ?? build.structureHeight,
      set: build.setStructureHeight,
      patch: (v) => ({ height: v }),
    },
    {
      label: 'Turn',
      id: 'structure-rotation',
      min: 0,
      max: 355,
      step: 5,
      float: false,
      unit: '°',
      value: selected?.rotation ?? build.structureRotation,
      set: build.setStructureRotation,
      patch: (v) => ({ rotation: v }),
    },
  ];
}

/** What is selected, and the way to delete it - or how to get one. */
function SelectedStructure({ build }) {
  const selected = build.selectedStructure;
  if (!selected) return <span className={styles.buildHint}>Click the map to place one · click one to select it</span>;
  return (
    <>
      <span className={styles.buildHint} data-testid="structure-selected">
        {selected.label || 'Structure'} selected - drag to move
      </span>
      <button type="button" className={common.btnSmall} data-testid="structure-delete" onClick={build.deleteStructure}>
        Delete
      </button>
    </>
  );
}

export default function StructurePanel({ build }) {
  return (
    <>
      <div className={styles.brushRow} role="radiogroup" aria-label="Structure shape">
        {STRUCTURE_SHAPES.map((sh) => (
          <RadioChip
            key={sh.key}
            checked={build.structureShape === sh.key}
            testId={`structure-${sh.key}`}
            onClick={() => build.setStructureShape(sh.key)}
            title={sh.hint}
          >
            {sh.label}
          </RadioChip>
        ))}
      </div>
      {structureSliders(build).map((s) => (
        <SliderInput
          key={s.id}
          label={s.label}
          data-testid={s.id}
          min={s.min}
          max={s.max}
          step={s.step}
          float={s.float}
          value={s.value}
          onChange={(value) => {
            s.set(value);
            if (build.selectedStructure) build.patchStructure(s.patch(value));
          }}
          unit={s.unit}
        />
      ))}
      <SelectedStructure build={build} />
    </>
  );
}
